import React, { useCallback, useEffect, useState } from "react";
import * as Icon from "react-feather";
import { getLanguage, subscribe } from "../language/LanguageSetting.js";
import {
  EXPIRED,
  checkSessionExpiry,
  dismissSessionNotice,
  onSessionNotice,
  reauthenticate,
} from "../services/session.mjs";

// How often a tab that is left open re-checks its own token.
const EXPIRY_POLL_MS = 30000;

/**
 * Mounted once by the layout, so every page shares it. Shows why the API
 * refused a request: an ended session sends the user back to log in, a
 * permission problem is only reported.
 */
const SessionNotice = () => {
  const [notice, setNotice] = useState(null);

  const [isFinnish, setIsFinnish] = useState(getLanguage());
  useEffect(() => {
    const unsubscribe = subscribe(() => {
      setIsFinnish(getLanguage());
    });
    return () => unsubscribe();
  }, []);

  const texts = isFinnish
    ? {
        istuntovanhentunut: "Istunto vanhentunut",
        istuntovanhentunutdesc: `Kirjautumisesi on vanhentunut turvallisuussyistä.
          Kirjaudu uudelleen jatkaaksesi.`,
        kirjauduuudelleen: "Kirjaudu uudelleen",
        eioikeutta: "Ei oikeutta",
        eioikeuttadesc: `Sinulla ei ole oikeutta tähän toimintoon. Kokeile
          kirjautua ulos ja takaisin sisään. Jos ongelma jatkuu, ota yhteyttä
          ylläpitoon.`,
        sulje: "Sulje",
      }
    : {
        istuntovanhentunut: "Session expired",
        istuntovanhentunutdesc: `Your login has expired for security reasons.
          Please log in again to continue.`,
        kirjauduuudelleen: "Log in again",
        eioikeutta: "Unauthorized",
        eioikeuttadesc: `You do not have permission for this action. Try
          logging out and back in. If the problem continues, please contact
          support.`,
        sulje: "Close",
      };

  useEffect(() => onSessionNotice(setNotice), []);

  useEffect(() => {
    checkSessionExpiry();
    const interval = window.setInterval(checkSessionExpiry, EXPIRY_POLL_MS);
    // A tab in the background does not run reliably, so re-check on return.
    window.addEventListener("focus", checkSessionExpiry);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", checkSessionExpiry);
    };
  }, []);

  const dismiss = useCallback(() => {
    setNotice(null);
    dismissSessionNotice();
  }, []);

  const logoutAndReauth = useCallback(() => {
    setNotice(null);
    reauthenticate();
  }, []);

  if (!notice) return null;

  const expired = notice.type === EXPIRED;

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-notice-title"
    >
      <div className="flex max-w-md flex-col gap-4 rounded-lg bg-white p-6 shadow-lg">
        <div className="flex items-center gap-3">
          {expired ? (
            <Icon.Clock className="h-auto w-8 flex-none text-blue-600" />
          ) : (
            <Icon.AlertTriangle className="h-auto w-8 flex-none text-red-500" />
          )}
          <h2 className="m-0 text-xl" id="session-notice-title">
            {expired ? texts.istuntovanhentunut : texts.eioikeutta}
          </h2>
        </div>
        <p className="m-0 text-gray-800">
          {expired ? texts.istuntovanhentunutdesc : texts.eioikeuttadesc}
        </p>
        <div className="flex justify-end gap-2">
          {!expired && (
            <button type="button" onClick={dismiss} className="btn -secondary">
              {texts.sulje}
            </button>
          )}
          <button
            type="button"
            onClick={expired ? dismiss : logoutAndReauth}
            className="btn -primary"
            autoFocus
          >
            {texts.kirjauduuudelleen}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionNotice;
